import { Box, Chip, IconButton, Stack, Radio, Typography, useTheme } from '@mui/material';
import { TeamMember } from 'apis/business/setting/team/types';
import Select, { Option } from 'components/Common/Select';
import { useMemo } from 'react';
import { Control, get, useFormContext } from 'react-hook-form';
import { RiDeleteBin6Line } from 'react-icons/ri';
import {
  buildSourceFromOperator,
  getOperatorDisplayName,
  getPreferredContactType,
  getTargetTypeLabel
} from './transferCallUtils';
import { MdOutlineModeEditOutline } from 'react-icons/md';

interface OperatorTransferEntryProps {
  index: number;
  parent: string;
  control: Control<any>;
  operatorOptions: Option[];
  operatorsData: TeamMember[];
  onScrollEnd?: () => void;
  label?: string;
  variant?: 'expanded' | 'summary';
  groupTransferMode?: string;
  showRemove?: boolean;
  onRemove?: () => void;
  onEdit?: () => void;
  excludedOperatorIds?: (string | number)[];
}

const cardStyle = () => ({
  border: '1px solid',
  borderColor: '#E5E7EB',
  borderRadius: 2,
  px: 2,
  py: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  transition: 'all 0.2s ease'
});

const summaryBadgeSx = {
  height: 28,
  bgcolor: '#F3F4F6',
  color: '#6B7280',

  fontSize: 13,
  fontWeight: 400,
  borderRadius: '16px',
  '& .MuiChip-label': { px: 1.5 }
};

export default function OperatorTransferEntry({
  index,
  parent,
  control,
  operatorOptions,
  operatorsData,
  onScrollEnd,
  label,
  variant = 'expanded',
  groupTransferMode = 'warm',
  showRemove,
  onRemove,
  onEdit,
  excludedOperatorIds = []
}: OperatorTransferEntryProps) {
  const theme = useTheme();
  const { watch, setValue, formState } = useFormContext();
  const fieldPrefix = `${parent}.sources.${index}`;
  const selectedOperatorId = watch(`${fieldPrefix}.target_id`);
  const targetType = watch(`${fieldPrefix}.target_type`);
  const targetName = watch(`${fieldPrefix}.target_name`);
  const targetValue = watch(`${fieldPrefix}.target_value`);
  const targetTypeError = get(formState.errors, `${fieldPrefix}.target_type.message`);

  const filteredOperatorOptions = useMemo(() => {
    if (excludedOperatorIds.length === 0) return operatorOptions;
    const excluded = new Set(excludedOperatorIds.map(String));
    return operatorOptions.filter(
      (option) => String(option.value) === String(selectedOperatorId) || !excluded.has(String(option.value))
    );
  }, [excludedOperatorIds, operatorOptions, selectedOperatorId]);

  const selectedOperator = useMemo(() => {
    if (selectedOperatorId === undefined || selectedOperatorId === null || selectedOperatorId === '') {
      return null;
    }
    return operatorsData.find((item) => String(item.id) === String(selectedOperatorId)) ?? null;
  }, [operatorsData, selectedOperatorId]);

  const operatorLabel =
    targetName ||
    (selectedOperator
      ? getOperatorDisplayName(selectedOperator) ||
        operatorOptions.find((opt) => String(opt.value) === String(selectedOperatorId))?.label ||
        'Operator'
      : 'Operator');

  const applyTargetType = (type: 'phone' | 'sip' | 'extension', operator?: TeamMember) => {
    const op = operator ?? selectedOperator;
    if (!op) return;
    const source = buildSourceFromOperator(op, type);
    setValue(`${fieldPrefix}.target_type`, source.target_type);
    setValue(`${fieldPrefix}.target_value`, source.target_value);
    setValue(`${fieldPrefix}.availability_schedule`, source.availability_schedule);
    setValue(`${fieldPrefix}.holidays`, source.holidays);

    if (
      index === 0 &&
      (parent.endsWith('action_details') || parent.includes('transfer_call_details')) &&
      watch(`${parent}.transfer_type`) === 'operator'
    ) {
      if (type === 'sip') {
        setValue(`${parent}.transfer_mode`, 'cold');
      } else if (type === 'extension') {
        setValue(`${parent}.transfer_mode`, 'warm');
      }
    }
  };

  if (variant === 'summary') {
    const contactLabel = getTargetTypeLabel(targetType);

    return (
      <Box mb={2}>
        {label && (
          <Typography fontWeight={600} fontSize={14} mb={1.5}>
            {label}
          </Typography>
        )}

        <Box display='flex' alignItems='center' mb={1.5}>
          <Box
            flex={1}
            sx={{
              border: '1px solid #E5E7EB',
              borderRadius: 1,
              px: 2,
              py: 1.25,
              bgcolor: '#FAFAFA'
            }}
          >
            <Typography fontSize={14} color='text.primary'>
              {operatorLabel}
            </Typography>
          </Box>
          {onEdit && (
            <IconButton size='small' onClick={onEdit} aria-label='Edit operator' sx={{ ml: 1 }}>
              <MdOutlineModeEditOutline size={20} color={theme.palette.primary.main} />
            </IconButton>
          )}
          {showRemove && onRemove && (
            <IconButton size='small' onClick={onRemove} aria-label='Remove operator'>
              <RiDeleteBin6Line size={19} color={theme.palette.error.main} />
            </IconButton>
          )}
        </Box>

        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
          {targetType && targetValue && (
            <Chip label={`${contactLabel}: ${targetValue}`} size='small' sx={summaryBadgeSx} />
          )}
          <Chip
            label={`Transfer: ${groupTransferMode === 'warm' ? 'Warm' : 'Cold'}`}
            size='small'
            sx={summaryBadgeSx}
          />
        </Stack>
      </Box>
    );
  }

  return (
    <Box my={2}>
      {label && (
        <Typography fontWeight={600} fontSize={14} mb={1.5}>
          {label}
        </Typography>
      )}

      <Box display='flex' alignItems='flex-start' gap={1} mb={2}>
        <Box flex={1}>
          <Select
            name={`${fieldPrefix}.target_id`}
            label='Operator'
            control={control}
            options={filteredOperatorOptions}
            onScrollEnd={onScrollEnd}
            fullWidth
            onChange={(e) => {
              const operatorId = e.target.value;
              const operator = operatorsData.find((item) => String(item.id) === String(operatorId));
              setValue(`${fieldPrefix}.target_id`, operatorId);
              setValue(`${fieldPrefix}.target_name`, operator ? getOperatorDisplayName(operator) : '');

              const preferredType = operator ? getPreferredContactType(operator) : null;
              if (operator && preferredType) {
                applyTargetType(preferredType, operator);
              } else {
                setValue(`${fieldPrefix}.target_type`, '');
                setValue(`${fieldPrefix}.target_value`, '');
                setValue(`${fieldPrefix}.availability_schedule`, undefined);
                setValue(`${fieldPrefix}.holidays`, undefined);
              }
            }}
          />
        </Box>
        {showRemove && onRemove && (
          <IconButton size='small' onClick={onRemove} aria-label='Remove operator' sx={{ mt: 1.5 }}>
            <RiDeleteBin6Line size={19} color={theme.palette.error.main} />
          </IconButton>
        )}
      </Box>

      {selectedOperator && (
        <>
          <Stack spacing={1} mb={2}>
            {selectedOperator.phone_number && (
              <Box
                onClick={() => {
                  applyTargetType('phone');
                }}
                sx={cardStyle()}
              >
                <Stack direction='row' alignItems='center' spacing={2} sx={{ height: '15px', ml: -1 }}>
                  <Radio checked={targetType === 'phone'} />
                  <Typography fontSize={16}>Phone Number</Typography>
                </Stack>
                <Typography fontSize={16} color='text.secondary'>
                  {selectedOperator.phone_number}
                </Typography>
              </Box>
            )}

            {selectedOperator.sip_uri && (
              <Box
                onClick={() => {
                  applyTargetType('sip');
                }}
                sx={cardStyle()}
              >
                <Stack direction='row' alignItems='center' spacing={2} sx={{ height: '15px', ml: -1 }}>
                  <Radio checked={targetType === 'sip'} />
                  <Typography fontSize={16}>SIP URI</Typography>
                </Stack>
                <Typography fontSize={16} color='text.secondary'>
                  {selectedOperator.sip_uri}
                </Typography>
              </Box>
            )}

            {selectedOperator.extension && (
              <Box
                onClick={() => {
                  applyTargetType('extension');
                }}
                sx={cardStyle()}
              >
                <Stack direction='row' alignItems='center' spacing={2} sx={{ height: '15px', ml: -1 }}>
                  <Radio checked={targetType === 'extension'} />
                  <Typography fontSize={16}>Extension</Typography>
                </Stack>
                <Typography fontSize={16} color='text.secondary'>
                  {selectedOperator.extension}
                </Typography>
              </Box>
            )}
          </Stack>
          {targetTypeError && (
            <Typography color='error' fontSize={12} sx={{ mt: -1, mb: 2, px: 1 }}>
              {String(targetTypeError)}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
